import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  HiOutlineUserGroup,
  HiOutlinePaperAirplane,
  HiOutlineLockClosed,
} from 'react-icons/hi2'
import { useAuth } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'
import ConnectionsList from './ConnectionsList'
import InviteEmployees from './InviteEmployees'
import ConnectionRequests from './ConnectionRequests'
import SendConnectionRequest from './SendConnectionRequest'
import {
  Button,
  Card,
  EmptyState,
  Modal,
  PageHeader,
} from '../../components/ui'

export default function ConnectionsPage() {
  const { t } = useTranslation()
  const { canManageConnections } = useAuth()
  const { getEntity } = useWorkspace()
  const [searchParams] = useSearchParams()

  const getInitialTab = () => {
    const action = searchParams.get('action')
    const tab = searchParams.get('tab')
    if (action === 'invite') return 'invite'
    if (tab === 'requests') return 'received-requests'
    if (tab === 'received-requests') return 'received-requests'
    if (tab === 'sent-requests') return 'sent-requests'
    return 'connections'
  }

  const [activeTab, setActiveTab] = useState(getInitialTab())
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showSendRequestModal, setShowSendRequestModal] = useState(false)

  useEffect(() => {
    setActiveTab(getInitialTab())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  if (!canManageConnections) {
    return (
      <Card padding="lg">
        <EmptyState
          icon={HiOutlineLockClosed}
          title={t('connections.unavailableTitle', { entity: getEntity('connection').singular })}
          description={t('connections.unavailableDescription', {
            entity: getEntity('connection').singular,
            workspace: getEntity('workspace').singular.toLowerCase(),
            employee: getEntity('employee').singular.toLowerCase(),
          })}
          action={<Button onClick={() => window.history.back()}>{t('common.back')}</Button>}
        />
      </Card>
    )
  }

  const tabs = [
    {
      id: 'connections',
      name: t('connections.tabConnections', { entity: getEntity('connection').plural }),
      description: t('connections.tabConnectionsDesc'),
    },
    {
      id: 'received-requests',
      name: t('connections.tabReceivedRequests'),
      description: t('connections.tabReceivedRequestsDesc'),
    },
    {
      id: 'sent-requests',
      name: t('connections.tabSentRequests'),
      description: t('connections.tabSentRequestsDesc', { entity: getEntity('connection').singular.toLowerCase() }),
    },
  ]

  const renderTabContent = () => {
    switch (activeTab) {
      case 'connections':       return <ConnectionsList />
      case 'received-requests': return <ConnectionRequests type="received" />
      case 'sent-requests':     return <ConnectionRequests type="sent" />
      default:                  return <ConnectionsList />
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={HiOutlineUserGroup}
        iconTone="brand"
        title={t('connections.title', { entity: getEntity('connection').singular })}
        subtitle={t('connections.subtitle', {
          entity: getEntity('connection').plural.toLowerCase(),
          employee: getEntity('employee').singular.toLowerCase(),
        })}
        actions={
          <Button variant="secondary" leadingIcon={HiOutlinePaperAirplane} onClick={() => setShowSendRequestModal(true)}>
            {t('connections.sendRequest')}
          </Button>
        }
      />

      <Card padding="none">
        <nav className="flex gap-1 px-3 sm:px-5 border-b border-border-subtle overflow-x-auto">
          {tabs.map((tab) => {
            const active = activeTab === tab.id
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-3 -mb-px border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  active
                    ? 'border-brand text-brand'
                    : 'border-transparent text-ink-secondary hover:text-ink-primary hover:border-border-strong'
                }`}
              >
                <div className="text-start">
                  <div>{tab.name}</div>
                  <div className="text-[11px] text-ink-muted mt-0.5 hidden sm:block">{tab.description}</div>
                </div>
              </button>
            )
          })}
        </nav>
        <div className="p-4 sm:p-6">
          {renderTabContent()}
        </div>
      </Card>

      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title={t('connections.invite.title', { entity: getEntity('employee').plural })}
        size="lg"
      >
        <div className="p-5 sm:p-6">
          <InviteEmployees onClose={() => setShowInviteModal(false)} />
        </div>
      </Modal>

      <Modal
        isOpen={showSendRequestModal}
        onClose={() => setShowSendRequestModal(false)}
        title={t('connections.send.submit', { conn: getEntity('connection').singular.toLowerCase() })}
        size="lg"
      >
        <div className="p-5 sm:p-6">
          <SendConnectionRequest onClose={() => setShowSendRequestModal(false)} />
        </div>
      </Modal>
    </div>
  )
}
